/**
 * Execution Agent Schema
 * 
 * Schema for the LLM-powered agent that builds slides area-by-area.
 * 
 * Key features:
 * - Pre-specified areas (caller determines regions, agent builds them)
 * - Two-level context (global for all slides, per-slide for specific content)
 * - Normalized regions (0-1) converted to points internally
 * - Multi-slide support (builds multiple slides in one agent run)
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// AREA DEFINITION (pre-specified by caller)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Region uses normalized coordinates (0-1) for easier specification.
 * ExecutionAgent converts to points using actual slide dimensions from slideDimensions param.
 */
export const RegionSchema = z.object({
    x: z.number().min(0).max(1).describe("Left position as fraction of slide width (0-1)"),
    y: z.number().min(0).max(1).describe("Top position as fraction of slide height (0-1)"),
    width: z.number().min(0).max(1).describe("Width as fraction of slide width (0-1)"),
    height: z.number().min(0).max(1).describe("Height as fraction of slide height (0-1)"),
});

export type Region = z.infer<typeof RegionSchema>;

export const AreaSchema = z.object({
    areaId: z.string().describe("Unique identifier for this area (e.g., 'header', 'main_chart', 'callouts')"),
    region: RegionSchema.describe("Bounding box for the area in normalized coordinates"),
    purpose: z.string().describe("What content should be built in this area"),
    contentType: z.enum(["text", "chart", "table", "image", "mixed"]).describe("Primary content type for this area"),
});

export type Area = z.infer<typeof AreaSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT ITEM (standard pattern from other agents)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Context uses the standard pattern from other agents.
 * IDs can be mixed types: as_xxx (AlphaSense), slide_5 (reference), excel_xxx, etc.
 */
export const ContextItemSchema = z.object({
    context: z.array(z.string()).describe("Array of context IDs (as_xxx, slide_N, excel_xxx, excelread_xxx, logo_xxx)"),
    purpose: z.string().describe("How to use this context"),
});

export type ContextItem = z.infer<typeof ContextItemSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// EXCEL CONTEXT (from LinkedExcelEditor)
// ═══════════════════════════════════════════════════════════════════════════

export const ExcelContextSchema = z.object({
    workbookPath: z.string().describe("Path to working workbook"),
    ranges: z.array(z.object({
        excelId: z.string(),
        sheetName: z.string(),
        range: z.string(),
        description: z.string().optional(),
    })).describe("Excel ranges/charts available for linking"),
    purpose: z.string().describe("Purpose of this Excel data"),
}).nullable();

export type ExcelContext = z.infer<typeof ExcelContextSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SLIDE TO BUILD
// ═══════════════════════════════════════════════════════════════════════════

export const SlideToBuildSchema = z.object({
    // NEW: Use layout-based insertion (preferred - uses pre-processed layout-only PPTX)
    layoutId: z.string().optional().describe("Layout ID from presentationLibrarySearchAgent (preferred)"),
    documentId: z.string().optional().describe("Deck ID (required if layoutId is provided)"),
    
    // DEPRECATED: Use layoutId instead. Kept for backwards compatibility.
    // If layoutId is provided, sourceSlideIndex is ignored.
    sourceSlideIndex: z.number().optional().describe("DEPRECATED: 0-based index of style guide slide. Use layoutId instead."),
    
    position: z.number().min(0).describe("Target position for the new slide (0-based, must be non-negative)"),
    title: z.string().describe("Title of the slide"),
    purpose: z.string().describe("What the slide should communicate"),
    areas: z.array(AreaSchema).describe("Areas to build on this slide"),
    context: z.array(ContextItemSchema).describe("Slide-specific context"),
}).refine(
    (data) => {
        // Must have either (layoutId AND documentId) OR sourceSlideIndex
        const hasLayoutMethod = data.layoutId !== undefined && data.documentId !== undefined;
        const hasSourceMethod = data.sourceSlideIndex !== undefined;
        return hasLayoutMethod || hasSourceMethod;
    },
    {
        message: "Must provide either (layoutId AND documentId) or sourceSlideIndex",
    }
).refine(
    (data) => {
        // If layoutId is provided, documentId must also be provided
        if (data.layoutId !== undefined && data.documentId === undefined) {
            return false;
        }
        return true;
    },
    {
        message: "documentId is required when layoutId is provided",
    }
);

export type SlideToBuild = z.infer<typeof SlideToBuildSchema>;

/**
 * Extended slide info with actual index after insertion
 */
export type SlideToBuildWithIndex = SlideToBuild & {
    actualSlideIndex: number;
};

// ═══════════════════════════════════════════════════════════════════════════
// SLIDE DIMENSIONS
// ═══════════════════════════════════════════════════════════════════════════

export const SlideDimensionsSchema = z.object({
    width: z.number().describe("Slide width in points"),
    height: z.number().describe("Slide height in points"),
});

export type SlideDimensions = z.infer<typeof SlideDimensionsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// EXECUTION AGENT PARAMS
// ═══════════════════════════════════════════════════════════════════════════

export const ExecutionAgentParamsSchema = z.object({
    planId: z.string().describe("Plan ID for this build session"),
    sectionId: z.string().optional().describe("Section ID (for tracking)"),
    sectionTitle: z.string().optional().describe("Section title (for display)"),
    uiDescription: z.string()
        .describe("Single-line UI description for top-of-card agent status display."),
    uiDescriptions: z.array(z.string())
        .optional()
        .describe("Optional list of UI description lines for the top section of agent-as-tool cards."),

    // Overall plan context - for understanding slide relationships
    overallPlan: z.string().optional()
        .describe("Full presentation plan text for context (helps avoid redundancy, understand flow)"),

    // Global context - applies to ALL slides
    globalContext: z.array(ContextItemSchema).optional()
        .describe("Global context that applies to all slides (e.g., brand logos, styling references)"),

    // Reference slides for styling (read-only, not copied)
    referenceSlideIndices: z.array(z.number()).optional()
        .describe("Style guide slide indices for visual reference (not copied)"),

    // Slides to build
    slides: z.array(SlideToBuildSchema).describe("Slides to build"),

    // Excel data (shared across slides)
    excelContext: ExcelContextSchema.optional().describe("Excel data for charts/tables"),

    // Working presentation path (set by MultiSlideBuilderAgent)
    workingPresentationPath: z.string().optional().describe("Path to working presentation"),

    // Slide dimensions (from working presentation, in points)
    slideDimensions: SlideDimensionsSchema.describe("Slide dimensions in points (e.g., 960x540 for widescreen)"),
    
    // Style guide slide count (for index offset calculation in readSlide tool)
    styleGuideSlideCount: z.number().int().nonnegative().optional().default(0)
        .describe("Number of style guide slides (for index offset when reading slides)"),
    
    // Visual guide generation options (optional - uses Gemini)
    generateVisualGuide: z.boolean().optional().default(false)
        .describe("If true, generate a visual blueprint using Gemini before building"),
    
    // Style guide info for visual guide generation (optional)
    styleGuideInfo: z.object({
        contentArea: z.object({
            left: z.number(),
            top: z.number(),
            width: z.number(),
            height: z.number(),
        }).optional(),
        exclusionZones: z.array(z.object({
            type: z.string(),
            bounds: z.object({
                left: z.number(),
                top: z.number(),
                width: z.number(),
                height: z.number(),
            }),
        })).optional(),
        topStyleSignatures: z.array(z.record(z.string(), z.any())).optional(),
    }).optional().describe("Style guide info for visual guide generation"),
    
    // Reference image base64 strings for visual guide (optional)
    referenceImages: z.array(z.string()).optional()
        .describe("Reference slide images as base64 for visual guide generation"),
    
    // Pre-generated visual guides from Gemini (one per slide)
    visualGuides: z.array(z.object({
        slideIndex: z.number().describe("Which slide this guide is for (0-based index in slides array)"),
        imageBase64: z.string().describe("Gemini-generated visual blueprint image"),
        instructions: z.string().optional().describe("Instructions used to generate this guide"),
    })).optional().describe("Pre-generated Gemini visual blueprints for each slide"),
});

export type ExecutionAgentParams = z.infer<typeof ExecutionAgentParamsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// AREA BUILD RESULT
// ═══════════════════════════════════════════════════════════════════════════

export const AreaBuildResultSchema = z.object({
    slideIndex: z.number(),
    areaId: z.string(),
    areaDescription: z.string(),
    checkpointId: z.string(),
    success: z.boolean(),
    shapesCreated: z.number(),
    errors: z.array(z.string()).optional(),
});

export type AreaBuildResult = z.infer<typeof AreaBuildResultSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SLIDE BUILD RESULT
// ═══════════════════════════════════════════════════════════════════════════

export const SlideBuildResultSchema = z.object({
    slideIndex: z.number().describe("Actual index after insertion"),
    title: z.string(),
    areasBuilt: z.number(),
    success: z.boolean(),
    errors: z.array(z.string()).optional(),
    finalCheckpointId: z.string().optional(),
});

export type SlideBuildResult = z.infer<typeof SlideBuildResultSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// EXECUTION AGENT RESPONSE
// ═══════════════════════════════════════════════════════════════════════════

export const ExecutionAgentResponseSchema = z.object({
    sectionId: z.string().optional(),
    sectionTitle: z.string().optional(),
    slidesBuilt: z.number(),
    slideResults: z.array(SlideBuildResultSchema),
    areaResults: z.array(AreaBuildResultSchema).optional().describe("Detailed area-by-area results"),
    success: z.boolean(),
    errors: z.array(z.string()).optional(),
});

export type ExecutionAgentResponse = z.infer<typeof ExecutionAgentResponseSchema>;

